/**
 * Workspace labels tab (FRIDAY-65).
 *
 * Labels are board-owned, so this screen is an AGGREGATION: it lists the
 * labels of every board the caller may read, each labelled with its owning
 * board, and every mutation targets the board that owns the label.
 */

import { useEffect, useState } from 'react'
import { ApiError } from '@/lib/api'
import { toast } from '@/features/feedback/toast'
import {
  useArchiveLabel,
  useCreateLabel,
  useUpdateLabel,
  useWorkspaceLabels,
  type WorkspaceLabel,
  type WorkspaceLabelBoard,
} from '@/features/labels/labels'
import { NavigationGuardProvider, useUnsavedChangesGuard } from '@/features/settings/useUnsavedChangesGuard'
import { ConfirmDialog } from '@/components/feedback'
import { cn } from '@/lib/utils'

const PAGE_SIZE = 25

export interface WorkspaceLabelsPanelProps {
  wsId: string
  /**
   * Locks the panel to one board. The board settings screen uses it to
   * show the labels of that board; the workspace settings leave it open.
   */
  projectId?: string
}

export function WorkspaceLabelsPanel({ wsId, projectId }: WorkspaceLabelsPanelProps) {
  return (
    <NavigationGuardProvider>
      <WorkspaceLabelsPanelBody wsId={wsId} projectId={projectId} />
    </NavigationGuardProvider>
  )
}

function WorkspaceLabelsPanelBody({ wsId, projectId: fixedProjectId }: WorkspaceLabelsPanelProps) {
  const [search, setSearch] = useState('')
  const [term, setTerm] = useState('')
  const [projectId, setProjectId] = useState(fixedProjectId ?? '')
  const [includeArchived, setIncludeArchived] = useState(false)
  const [offset, setOffset] = useState(0)

  // Debounce the search term so a fast typist does not fire a request per
  // keystroke.
  useEffect(() => {
    const timer = setTimeout(() => setTerm(search.trim()), 250)
    return () => clearTimeout(timer)
  }, [search])

  const labelsQ = useWorkspaceLabels(wsId, {
    q: term,
    projectId: projectId || undefined,
    includeArchived,
    limit: PAGE_SIZE,
    offset,
  })

  const boards = labelsQ.data?.boards ?? []
  const labels = labelsQ.data?.labels ?? []
  const total = labelsQ.data?.total ?? 0

  // A board filter that points at a board the caller can no longer see (a
  // workspace switch, an archived board) must not leave an empty screen
  // behind — clear it instead.
  useEffect(() => {
    if (!projectId || fixedProjectId) return
    if (!labelsQ.data) return
    if (!boards.some((b) => b.id === projectId)) setProjectId('')
  }, [projectId, boards, labelsQ.data])

  // A search or a filter change starts a new result set.
  useEffect(() => {
    setOffset(0)
  }, [term, projectId, includeArchived])

  if (labelsQ.isLoading) {
    return (
      <p className="text-surface-500 p-4 text-sm" role="status">
        Loading labels…
      </p>
    )
  }

  if (labelsQ.isError) {
    return (
      <div className="p-4" role="alert">
        <p className="text-sm text-red-600 dark:text-red-300">Could not load the labels of this workspace.</p>
        <button type="button" className="btn-ghost mt-2" onClick={() => void labelsQ.refetch()}>
          Retry
        </button>
      </div>
    )
  }

  const boardById = new Map(boards.map((b) => [b.id, b] as const))

  return (
    <div className="space-y-4">
      <p className="text-surface-500 text-xs">
        Labels belong to a board — they are shown here from every board in this workspace. Editing a label
        changes it on its own board.
      </p>

      <section className="card p-4" aria-label="Filter labels">
        <div className="flex flex-wrap items-end gap-2">
          <label className="flex-1 min-w-[12rem]">
            <span className="block text-xs font-medium text-surface-600 dark:text-surface-300">Search</span>
            <input
              type="search"
              className="input mt-1"
              placeholder="Label name"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </label>
          <label>
            <span className="block text-xs font-medium text-surface-600 dark:text-surface-300">Board</span>
            <select
              className="input mt-1"
              value={projectId}
              disabled={!!fixedProjectId}
              onChange={(e) => setProjectId(e.target.value)}
            >
              <option value="">All boards</option>
              {boards.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name} ({b.key})
                </option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-2 pb-2 text-xs text-surface-600 dark:text-surface-300">
            <input
              type="checkbox"
              className="h-4 w-4"
              checked={includeArchived}
              onChange={(e) => setIncludeArchived(e.target.checked)}
            />
            Show archived
          </label>
        </div>
      </section>

      <CreateLabelSection wsId={wsId} boards={boards} defaultProjectId={projectId} />

      {labels.length === 0 ? (
        <div className="rounded-lg border border-dashed border-surface-200 p-12 text-center text-sm text-surface-500 dark:border-surface-700">
          No labels yet. Create one on a board to start tagging tasks.
        </div>
      ) : (
        <ul className="space-y-2" aria-label="Labels">
          {labels.map((label) => (
            <LabelRow key={label.id} wsId={wsId} label={label} board={boardById.get(label.projectId)} />
          ))}
        </ul>
      )}

      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between text-xs text-surface-500">
          <span>
            Showing {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total}
          </span>
          <span className="flex gap-2">
            <button
              type="button"
              className="btn-ghost min-h-11"
              disabled={offset === 0 || labelsQ.isFetching}
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            >
              Previous
            </button>
            <button
              type="button"
              className="btn-ghost min-h-11"
              disabled={offset + PAGE_SIZE >= total || labelsQ.isFetching}
              onClick={() => setOffset(offset + PAGE_SIZE)}
            >
              Next
            </button>
          </span>
        </div>
      )}
    </div>
  )
}

/** Create a label on one of the boards the caller may write to. */
function CreateLabelSection({
  wsId,
  boards,
  defaultProjectId,
}: {
  wsId: string
  boards: WorkspaceLabelBoard[]
  defaultProjectId: string
}) {
  const writable = boards.filter((b) => b.canWrite)
  const [projectId, setProjectId] = useState(defaultProjectId)
  const [name, setName] = useState('')
  const [color, setColor] = useState('#9698F9')
  const [error, setError] = useState<string | null>(null)
  const create = useCreateLabel(wsId, projectId)

  // Follow the board filter until the user picks a board themselves.
  const [picked, setPicked] = useState(false)
  useEffect(() => {
    if (picked) return
    setProjectId(defaultProjectId)
  }, [defaultProjectId, picked])

  const dirty = name.trim().length > 0
  useUnsavedChangesGuard(dirty && !create.isPending)

  if (writable.length === 0) {
    return (
      <p className="text-surface-500 text-xs">
        You cannot create labels here: none of the boards in this workspace accept label changes for your
        role.
      </p>
    )
  }

  return (
    <section className="card p-4" aria-label="Create label">
      <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-surface-500">New label</h2>
      <form
        className="flex flex-wrap items-end gap-2"
        onSubmit={async (e) => {
          e.preventDefault()
          if (!name.trim() || !projectId) return
          setError(null)
          try {
            await create.mutateAsync({ name: name.trim(), color })
            setName('')
            toast('Label created', { kind: 'success' })
          } catch (err) {
            const msg = err instanceof ApiError ? err.message : (err as Error).message
            setError(msg)
            toast(msg, { kind: 'error' })
          }
        }}
      >
        <label>
          <span className="block text-xs font-medium text-surface-600 dark:text-surface-300">Board</span>
          <select
            className="input mt-1"
            value={projectId}
            onChange={(e) => {
              setPicked(true)
              setProjectId(e.target.value)
            }}
          >
            <option value="">Pick a board…</option>
            {writable.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name} ({b.key})
              </option>
            ))}
          </select>
        </label>
        <label className="flex-1 min-w-[12rem]">
          <span className="block text-xs font-medium text-surface-600 dark:text-surface-300">Name</span>
          <input
            className="input mt-1"
            placeholder="e.g. Bug"
            maxLength={40}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </label>
        <label>
          <span className="block text-xs font-medium text-surface-600 dark:text-surface-300">Colour</span>
          <input
            type="color"
            aria-label="Colour"
            className="mt-1 h-9 w-12 cursor-pointer rounded border border-surface-300 dark:border-surface-700"
            value={color}
            onChange={(e) => setColor(e.target.value)}
          />
        </label>
        <button type="submit" className="btn-primary" disabled={create.isPending || !name.trim() || !projectId}>
          {create.isPending ? 'Creating…' : 'Create label'}
        </button>
      </form>
      {error && (
        <p role="alert" className="mt-2 text-sm text-red-600 dark:text-red-300">
          {error}
        </p>
      )}
    </section>
  )
}

/** One label: name, colour, owning board, usage and the edit/archive actions. */
function LabelRow({
  wsId,
  label,
  board,
}: {
  wsId: string
  label: WorkspaceLabel
  board?: WorkspaceLabelBoard
}) {
  const [editing, setEditing] = useState(false)
  const [name, setName] = useState(label.name)
  const [error, setError] = useState<string | null>(null)
  const [pendingArchive, setPendingArchive] = useState(false)
  const update = useUpdateLabel(wsId, label.projectId)
  const archive = useArchiveLabel(wsId, label.projectId)

  const canWrite = board?.canWrite ?? false
  const dirty = editing && name.trim() !== label.name
  useUnsavedChangesGuard(dirty && !update.isPending)

  function cancel() {
    setName(label.name)
    setEditing(false)
    setError(null)
  }

  async function save() {
    if (!dirty) return
    setError(null)
    try {
      await update.mutateAsync({ labelId: label.id, patch: { name: name.trim() } })
      setEditing(false)
      toast('Label updated', { kind: 'success' })
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : 'Could not update the label.'
      setError(msg)
      toast(msg, { kind: 'error' })
    }
  }

  function recolour(color: string) {
    if (color === label.color) return
    update.mutate(
      { labelId: label.id, patch: { color } },
      {
        onSuccess: () => toast('Label updated', { kind: 'success' }),
        onError: (e) => {
          const msg = e instanceof ApiError ? e.message : 'Could not update the label.'
          setError(msg)
          toast(msg, { kind: 'error' })
        },
      },
    )
  }

  return (
    <li className="card p-3" data-testid="workspace-label">
      <div className="flex flex-wrap items-center gap-3">
        <span
          className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full"
          style={{ backgroundColor: label.color }}
          aria-hidden="true"
        />
        <div className="min-w-0 flex-1">
          {editing ? (
            <input
              className="input h-7 py-0 text-sm"
              aria-label={`Name for ${label.name}`}
              maxLength={40}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          ) : (
            <div className="truncate text-sm font-semibold">
              {label.name}
              {label.archivedAt && (
                <span className="ml-2 rounded bg-surface-200 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-surface-600 dark:bg-surface-700 dark:text-surface-300">
                  Archived
                </span>
              )}
            </div>
          )}
          <div className="text-xs text-surface-500">
            {label.projectName} ({label.projectKey}) · {label.taskCount}{' '}
            {label.taskCount === 1 ? 'task' : 'tasks'}
          </div>
        </div>

        {canWrite && (
          <div className="flex items-center gap-1">
            <label className="flex items-center gap-1" title="Change colour">
              <span className="sr-only">Colour for {label.name}</span>
              <input
                type="color"
                aria-label={`Colour for ${label.name}`}
                className="h-8 w-9 cursor-pointer rounded border border-surface-300 dark:border-surface-700"
                value={label.color}
                onChange={(e) => recolour(e.target.value)}
              />
            </label>
            <button type="button" className="btn-ghost min-h-11 text-xs" onClick={() => setEditing(true)}>
              Rename
            </button>
            <button
              type="button"
              className="btn-ghost min-h-11 text-xs text-red-600 dark:text-red-300"
              onClick={() => setPendingArchive(true)}
            >
              Archive
            </button>
          </div>
        )}
      </div>

      {editing && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <button
            type="button"
            className="btn-primary min-h-11 text-xs"
            disabled={update.isPending || !dirty}
            onClick={() => void save()}
          >
            {update.isPending ? 'Saving…' : 'Save'}
          </button>
          <button type="button" className="btn-ghost min-h-11 text-xs" disabled={update.isPending} onClick={cancel}>
            Cancel
          </button>
        </div>
      )}

      {error && (
        <p role="alert" className="mt-2 text-sm text-red-600 dark:text-red-300">
          {error}
        </p>
      )}

      <ConfirmDialog
        open={pendingArchive}
        title={`Archive "${label.name}"?`}
        body={
          <div className="text-surface-600 dark:text-surface-300 space-y-2 text-sm">
            <p>
              This hides the label from the label list of {label.projectName} ({label.projectKey}). It is not
              deleted, and archived labels cannot be brought back from Friday.
            </p>
            <p>
              {label.taskCount === 0
                ? 'No task is tagged with this label.'
                : `${label.taskCount} ${label.taskCount === 1 ? 'task is' : 'tasks are'} still tagged with it and keep showing the label until it is removed from them.`}
            </p>
          </div>
        }
        confirmLabel="Archive label"
        destructive
        busy={archive.isPending}
        onClose={() => !archive.isPending && setPendingArchive(false)}
        onConfirm={async () => {
          try {
            await archive.mutateAsync(label.id)
            setPendingArchive(false)
            toast(`Label "${label.name}" archived`, { kind: 'success' })
          } catch (e) {
            const msg = e instanceof ApiError ? e.message : 'Could not archive the label.'
            setError(msg)
            toast(msg, { kind: 'error' })
          }
        }}
      />
    </li>
  )
}
